/**
 * How a clip behaves: a loop you glance at, or a film you watch.
 *
 * One rule for the whole page, because the answer belongs to the file rather
 * than to the block it happens to sit in. Decided in one renderer and left to
 * the others, it drifts: a sixty-second clip ends up autoplaying on a loop in
 * the middle of an article, which is the one thing the rule exists to prevent.
 *
 * Use `videoFigure(item)` wherever a skin renders a video and the behaviour
 * will be consistent, including for videos an editor adds later.
 */

import { RemidusVideo, type MediaItem } from '@remidus/web-core'

/** Above this many seconds a clip is a film and gets controls; below it, it is
 *  an observation and behaves like a GIF. A page of minute-long recordings on
 *  loop is unwatchable and a waste of a phone's battery. */
export const FILM_SECONDS = 20

/** Unknown duration is treated as a film.
 *
 *  The two failures are not equal: a play button on a three-second clip is a
 *  small loss, and a minute of video autoplaying in a loop is a real one. When
 *  the metadata cannot say, take the cheaper mistake. */
export function isFilm(item: MediaItem): boolean {
  return !(typeof item.duration === 'number') || item.duration > FILM_SECONDS
}

/** A video element with the behaviour its own duration earns. */
export function videoFigure(item: MediaItem): HTMLElement {
  const film = isFilm(item)
  const figure = RemidusVideo(item, { autoplay: !film, loop: !film, controls: film })
  if (film) playOnFocus(figure)
  return figure
}

/**
 * A film starts itself when it is in front of the reader.
 *
 * Muted, because autoplay only survives muted and the controls are right there
 * for sound; paused again the moment it scrolls away, because a film playing to
 * nobody is a phone getting warm. A reader who pauses one has answered the
 * question, and it stays paused until they press play — the page never argues
 * with a hand that has already spoken.
 *
 * Under `prefers-reduced-motion` nothing starts itself: the play button is the
 * whole interface, as the skin contract requires.
 */
function playOnFocus(figure: HTMLElement): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  const video = figure.querySelector<HTMLVideoElement>('video')
  if (!video) return
  video.muted = true

  let userPaused = false
  let autoPausing = false
  video.addEventListener('pause', () => {
    // The pause event is queued, not synchronous, so the flag is consumed here
    // rather than reset after the pause() call it belongs to. Resetting it
    // there misread every automatic pause as the reader's, and a film that had
    // scrolled away once would never start again.
    if (autoPausing) {
      autoPausing = false
      return
    }
    if (!video.ended) userPaused = true
  })
  video.addEventListener('play', () => {
    userPaused = false
  })

  const seen = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          if (!userPaused && video.paused) void video.play().catch(() => undefined)
        } else if (!video.paused) {
          autoPausing = true
          video.pause()
        }
      }
    },
    // Half the frame: enough that the film is what the reader is looking at,
    // not a sliver crossing the fold.
    { threshold: 0.5 },
  )
  seen.observe(video)
}
